'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { APP_CONSTANTS } from '../../../constants/app.constants';

export function LandingNavbar() {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when pathname changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About' },
    { href: '/features', label: 'Features' },
    { href: '/contact-us', label: 'Contact Us' },
  ];

  const isActive = (href: string) => {
    return href === '/' ? pathname === '/' : pathname.startsWith(href);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E4E8EA]/50 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between h-[var(--top-navbar-height,64px)]">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center gap-1.5 xs:gap-2 sm:gap-2.5 group transition-opacity hover:opacity-90 z-10 flex-shrink-0"
          >
            <div className="flex-shrink-0">
              <img src={APP_CONSTANTS.APP_LOGO_WITH_NAME} className="h-5 xs:h-6 sm:h-7 md:h-8 lg:h-9 w-auto" />
            </div>

          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center gap-0.5 xl:gap-1 absolute left-1/2 transform -translate-x-1/2">
            {navLinks.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`relative px-2.5 xl:px-3 2xl:px-4 py-2 text-xs xl:text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    active
                      ? 'text-[#0D373D] bg-[#E2FDCB]/50'
                      : 'text-[#4D4F52] hover:text-[#0D373D] hover:bg-[#F5F8F9]'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Tablet Navigation - Simplified */}
          <div className="hidden md:flex lg:hidden items-center gap-1 absolute left-1/2 transform -translate-x-1/2">
            {navLinks.slice(0, 3).map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`relative px-2 py-2 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    active
                      ? 'text-[#0D373D] bg-[#E2FDCB]/50'
                      : 'text-[#4D4F52] hover:text-[#0D373D] hover:bg-[#F5F8F9]'
                  }`}
                >
                  {link.href === '/parent-app' ? 'Parent' : link.label}
                </Link>
              );
            })}
            {/* More Menu for Tablet */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-[#4D4F52] hover:text-[#0D373D] hover:bg-[#F5F8F9] transition-colors"
              aria-label="More menu"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-1.5 xs:p-2 rounded-lg text-[#4D4F52] hover:text-[#0D373D] hover:bg-[#F5F8F9] transition-colors z-10"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-5 xs:h-6 w-5 xs:w-6" />
            ) : (
              <Menu className="h-5 xs:h-6 w-5 xs:w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E4E8EA]/50 py-3 xs:py-4 animate-fadeIn">
            <div className="flex flex-col gap-0.5 xs:gap-1">
              {navLinks.map((link) => {
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-3 xs:px-4 py-2.5 xs:py-3 text-sm xs:text-base font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                      active
                        ? 'text-[#0D373D] bg-[#E2FDCB]/50'
                        : 'text-[#4D4F52] hover:text-[#0D373D] hover:bg-[#F5F8F9]'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Tablet Overflow Menu */}
        {mobileMenuOpen && (
          <div className="hidden md:block lg:hidden border-t border-[#E4E8EA]/50 py-3 animate-fadeIn">
            <div className="flex flex-col gap-1">
              {navLinks.slice(3).map((link) => {
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                      active
                        ? 'text-[#0D373D] bg-[#E2FDCB]/50'
                        : 'text-[#4D4F52] hover:text-[#0D373D] hover:bg-[#F5F8F9]'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}





